import React, { useState, useMemo } from 'react';
import {
	Box,
	Button,
	ButtonProps,
	Grow,
	Hidden,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	styled,
	Tooltip,
	tooltipClasses,
	TooltipProps,
	Typography,
} from '@mui/material';
import { GridRowModel, GridState, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { RiArrowDownSFill, RiArrowUpSFill, RiCloseLine } from 'react-icons/ri';
import Container from 'components/Container';
import { useTranslation } from 'services/localization';
import { getDataGridLocales } from 'services/localization/getDataGridLocales';

export interface DataGridBulkAction<RowType extends GridRowModel> {
	key: DataGridBulkActionKey;
	label: string;
	handler: (rowIds: string[]) => void | Promise<void>;
	permissionsFields?: Array<keyof RowType>;
}

export interface DataGridDeleteAction<RowType extends GridRowModel> {
	enabled: boolean;
	handler: (rowIds: string[]) => void;
	permissionsFields?: Array<keyof RowType>;
}

export interface DataGridExportAction<RowType extends GridRowModel> {
	enabled: boolean;
	handler?: (rowIds: string[]) => void;
	permissionsFields?: Array<keyof RowType>;
}

type DataGridBulkActionKey = Exclude<string, 'delete' | 'export'>;
type DataGridActionKey = DataGridBulkActionKey | 'delete' | 'export';

export interface DataGridSelectedRowsActionsProps<RowType extends GridRowModel> {
	exportFileName?: string;
	actions?: DataGridBulkAction<RowType>[];
	deleteAction?: DataGridDeleteAction<RowType>;
	exportAction?: DataGridExportAction<RowType>;
	getRowLabel?: (row: RowType) => string;
}
const DataGridSelectedRowsActions = <RowType extends GridRowModel>(
	props: DataGridSelectedRowsActionsProps<RowType>,
) => {
	const { t } = useTranslation();
	const { actions = [], deleteAction, exportAction, exportFileName, getRowLabel } = props;

	const apiRef = useGridApiContext();
	const dataGridLocales = useMemo(() => getDataGridLocales(t), [t]);

	const selectedRowIds = useGridSelector(apiRef, (state: GridState) =>
		Array.isArray(state.selection)
			? state.selection.map((id) => String(id))
			: [String(state.selection)],
	);

	const rowsLookup = useGridSelector(apiRef, (state: GridState) => state.rows.idRowsLookup);

	const selectedRowsCount = selectedRowIds.length;
	const isOpen = selectedRowsCount > 0;

	const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement | null>(null);

	const isActionAllowed = (
		actionKey: DataGridActionKey,
	): { allowed: boolean; disallowedRowIds: string[] } => {
		const fieldsToCheck = (() => {
			switch (actionKey) {
				case 'delete':
					return deleteAction?.permissionsFields;
				case 'export':
					return exportAction?.permissionsFields;
				default: {
					return actions.find((action) => action.key === actionKey)?.permissionsFields;
				}
			}
		})();

		if (!fieldsToCheck)
			return {
				allowed: true,
				disallowedRowIds: [],
			};

		const disallowedRowIds = selectedRowIds.filter((id) =>
			fieldsToCheck.some((field) => !rowsLookup[id][field as string]),
		);

		return {
			allowed: disallowedRowIds.length === 0,
			disallowedRowIds,
		};
	};

	const handleClear = () => {
		apiRef.current.setSelectionModel([]);
	};

	const getDisallowedText = (rowIds: string[]) => {
		const rowNames = rowIds.map((id) => {
			if (!!getRowLabel) {
				const row = rowsLookup[id] as RowType;
				if (!!row) {
					return getRowLabel(row);
				}
			}
			return id;
		});

		const rowNamesText =
			rowNames.length > 2
				? `${rowNames.slice(0, 2).join(', ')}, ...+${rowNames.length - 2}`
				: `${rowNames.join(', ')}`;

		return t('dataGrid.thisActionIsNotAllowedFor', {
			rowNames: rowNamesText,
			defaultValue: 'This action is not allowed for: {{rowNames}}',
		});
	};

	const deleteActionProps = isActionAllowed('delete');

	return (
		<Box
			sx={{
				position: 'fixed',
				bottom: (theme) => theme.spacing(2),
				left: 0,
				right: 0,
				width: '100%',
				zIndex: (theme) => theme.zIndex.drawer - 1,
			}}
		>
			<Container noVerticalPadding>
				<Grow in={isOpen}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						spacing={2}
						sx={{
							width: '100%',
							background: (theme) => theme.palette.info.darker,
							p: 2,
							color: (theme) => theme.palette.info.contrastText,
							borderRadius: '4px',
							boxShadow: (theme) => theme.shadows['6'],
						}}
					>
						<Stack direction="row" alignItems="center" spacing={1}>
							<IconButton
								sx={{ color: (theme) => theme.palette.info.contrastText }}
								onClick={handleClear}
							>
								<RiCloseLine />
							</IconButton>
							<Typography variant="body1">
								{dataGridLocales.footerRowSelected?.(selectedRowsCount)}
							</Typography>
						</Stack>
						<Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
							<Hidden mdDown>
								{!!exportAction?.enabled && (
									<Button
										variant="contained"
										color="secondary"
										onClick={() => {
											if (!!exportAction.handler) {
												exportAction.handler(selectedRowIds);
											} else {
												apiRef.current.exportDataAsCsv({ fileName: exportFileName });
											}
										}}
									>
										{dataGridLocales.toolbarExport}
									</Button>
								)}
							</Hidden>

							{!!actions?.length && (
								<>
									<Button
										variant="contained"
										color="secondary"
										onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
										endIcon={Boolean(actionsMenuAnchor) ? <RiArrowDownSFill /> : <RiArrowUpSFill />}
									>
										{t('common:actions.actions', 'Actions')}
									</Button>

									<Menu
										anchorEl={actionsMenuAnchor}
										anchorOrigin={{
											vertical: 'top',
											horizontal: 'center',
										}}
										transformOrigin={{
											vertical: 'bottom',
											horizontal: 'center',
										}}
										open={Boolean(actionsMenuAnchor)}
										onClose={() => setActionsMenuAnchor(null)}
										PaperProps={{
											sx: {
												minWidth: 200,
											},
										}}
									>
										{actions.map((action) => {
											const { allowed, disallowedRowIds } = isActionAllowed(action.key);
											return (
												<MenuItemTooltip
													title={!allowed ? getDisallowedText(disallowedRowIds) : ''}
												>
													<Box
													/* This is necesary for the tooltip to work when the menu item is disabled
													 */
													>
														<MenuItem
															onClick={() => {
																if (!allowed) return;
																action.handler(selectedRowIds);
																setActionsMenuAnchor(null);
															}}
															disabled={!allowed}
														>
															{action.label}
														</MenuItem>
													</Box>
												</MenuItemTooltip>
											);
										})}
										<Hidden mdUp>
											{!!exportAction?.enabled && (
												<MenuItem
													onClick={() => {
														if (!!exportAction.handler) {
															exportAction.handler(selectedRowIds);
														} else {
															apiRef.current.exportDataAsCsv({ fileName: exportFileName });
														}
													}}
												>
													{dataGridLocales.toolbarExport}
												</MenuItem>
											)}
										</Hidden>
										<Hidden mdUp>
											{!!deleteAction?.enabled && (
												<MenuItemTooltip
													title={
														!deleteActionProps.allowed
															? getDisallowedText(deleteActionProps.disallowedRowIds)
															: ''
													}
												>
													<Box
													/* This is necesary for the tooltip to work when the menu item is disabled
													 */
													>
														<MenuItem
															onClick={() => deleteAction.handler(selectedRowIds)}
															disabled={!deleteActionProps.allowed}
															sx={{ color: (theme) => theme.palette.error.main }}
														>
															{t('common:actions.delete')}
														</MenuItem>
													</Box>
												</MenuItemTooltip>
											)}
										</Hidden>
									</Menu>
								</>
							)}
							<Hidden mdDown>
								{!!deleteAction?.enabled && (
									<Tooltip
										title={
											!deleteActionProps.allowed
												? getDisallowedText(deleteActionProps.disallowedRowIds)
												: ''
										}
									>
										<Box>
											<DeleteButton
												onClick={() => deleteAction.handler(selectedRowIds)}
												disabled={!deleteActionProps.allowed}
											>
												{t('common:actions.delete')}
											</DeleteButton>
										</Box>
									</Tooltip>
								)}
							</Hidden>
						</Stack>
					</Stack>
				</Grow>
			</Container>
		</Box>
	);
};

const DeleteButton = styled(({ className, ...props }: ButtonProps) => (
	<Button {...props} color="error" variant="contained" classes={{ disabled: className }} />
))(({ theme }) => ({
	background: `${theme.palette.error.main} !important`,
	opacity: 0.5,
}));

const MenuItemTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} enterTouchDelay={0} placement="left" classes={{ popper: className }} />
))({
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 'calc(100vw - 300px)',
	},
});

export default DataGridSelectedRowsActions;
